import GrainIcon from '@material-ui/icons/Grain';
import ReorderIcon from '@material-ui/icons/Reorder';
import { Typography } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import LocationSearchIcon from '@material-ui/icons/LocationSearching';
import WarningIcon from '@material-ui/icons/Warning';
import PropTypes from 'prop-types';
import { default as React } from 'react';
import { withRouter } from 'react-router-dom';
import { APPLICATION_PATHS } from '../../application-core/constants';
import CancelButton from '../../core/CancelButton';
import Errored from '../../core/Errored';
import PrimaryButton from '../../core/PrimaryButton';
import SecondaryButton from '../../core/SecondaryButton';
import { globalStyles as styles } from '../../core/style/gstheme';
import TitledPanel from '../../core/TitledPanel';
import TitledTextField from '../../core/TitledTextField';
import { MAX_LONGIRI_LENGTH } from '../../core/utils/constants';
import { detailedCheckIRI, makeCancelable, sanitiseText } from '../../core/utils/utils';
import GS from '../../grafsync/grafsync';
import SchemeChip from '../../vocabulary/core/SchemeChip';

class FindIdentifierDialog extends React.Component {

    state = {
        open: false,
        checking: false,
        iri: "",
        exists: false,
        checked: false,
        isDirty: false,
        resourceType: "",
        isValidIRI: false,
        resourceSchemes: []
    };

    handleClickOpen = () => {
        this.setState({
            open: true,
            iri: "",
            checking: false,
            exists: false,
            checked: false,
            isDirty: false,
            isValidIR: false
        });
    };

    exitFindIRI = () => { this.setState({ open: false }); };

    doFind(payload) {
        this.cancellable = makeCancelable(GS.find(payload));
        return this.cancellable;
    }

    findIRI = () => {
        this.setState({
            checking: true,
            checked: false
        }, () => {
            const { iri } = this.state;
            const { graph } = this.props;
            this
                .doFind({
                    value: iri,
                    graph
                })
                .promise
                .then((data) => {
                    const exists = data && data.exists;
                    if (exists) {
                        let resourceType = "";
                        let resourceSchemes = [];
                        let iriToLoad = data.loadValue || iri;
                        let editedIRI = null;
                        GS.loadData([iriToLoad], {}, graph).then(() => {
                            if (GS.isLoaded(iriToLoad, graph)) {
                                const data = GS.data(iriToLoad, graph);
                                if (data.iri && data.iri.length > 0) {
                                    editedIRI = data.iri[data.iri.length - 1];
                                }
                                if (data.type.includes("ConceptScheme")) {
                                    resourceType = "Concept Scheme";
                                    resourceSchemes = [iriToLoad];
                                }
                                else if (data.type.includes("OrderedCollection") || data.type.includes("Collection")) {
                                    resourceType = "Collection";
                                    resourceSchemes = [];
                                }
                                else if (data.type.includes("Concept")) {
                                    resourceType = "Concept";
                                    if (data.inScheme) {
                                        resourceSchemes = Array.isArray(data.inScheme) ? data.inScheme : [data.inScheme];
                                    }
                                }
                                else if (data.type.includes("Label")) {
                                    resourceType = "Label";
                                    if (data.inScheme) {
                                        resourceSchemes = Array.isArray(data.inScheme) ? data.inScheme : [data.inScheme];
                                    }
                                }
                            }
                            this.setState({
                                exists: exists,
                                checked: true,
                                checking: false,
                                isDirty: false,
                                iriToLoad,
                                editedIRI,
                                resourceType,
                                resourceSchemes
                            });
                        });
                    }
                    else {
                        this.setState({
                            checking: false,
                            checked: true,
                            isDirty: false
                        });
                    }
                })
                .catch((e) => {
                    if (e.message === "The requested IRI cannot be processed.") {
                        this.setState({
                            checking: false,
                            checked: true,
                            isDirty: false,
                            isValidIRI: false
                        });
                        return;
                    }
                    if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
                });
        });
    };

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    handleIRIChange = event => {
        const t = event.target;
        const text = sanitiseText(t.value.trim());
        let isValidIRI = true;
        if (!detailedCheckIRI(text)) {
            isValidIRI = false;
        }
        const isDirty = text !== this.state.iri;
        this.setState({ iri: text, isValidIRI, isDirty, resourceType: "", checked: false });
    };

    goToFloating = (resourceType) => {
        let link = APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/floating#project=' + this.props.project;
        if (resourceType === "Concept") link += "&view=concept&concept=" + this.state.iri;
        if (resourceType === "Label") link += "&view=label&label=" + this.state.iri;
        this.props.history.push(link);
    };

    render() {

        const { classes, graph, defaultLang, project, schemes, collections } = this.props;
        const { iri, iriToLoad, isErrored, isValidIRI, checking, resourceType, isDirty, resourceSchemes, exists, checked, editedIRI } = this.state;

        if (isErrored) return <Errored data={isErrored} />;

        let icon = null;
        let additionalIcon = null;
        if (!isValidIRI && iri.length > 0) {
            icon =
                <InputAdornment position="end">
                    <Tooltip title="The value is invalid">
                        <WarningIcon data-test="warning" />
                    </Tooltip>
                </InputAdornment>;
        }

        return (
            <React.Fragment>
                <span style={{ marginRight: 6 }}>
                    <Tooltip title="Find by IRI">
                        <IconButton className={classes.smallIconButton} onClick={this.handleClickOpen}>
                            <LocationSearchIcon data-test="find-iri" fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </span>
                <Dialog
                    data-test="find-iri-dialog"
                    maxWidth="md"
                    fullWidth
                    classes={{
                        paper: classes.paper,
                        paperWidthMd: classes.mediumDialog
                    }}
                    open={this.state.open} onClose={this.exitAddIRI} aria-labelledby="iri-dialog-title">
                    <DialogTitle disableTypography className={classes.mainHeading} id="iri-dialog-title">Find by IRI</DialogTitle>
                    <DialogContent style={{ minHeight: '280px' }}>
                        <div data-test="find-iri">
                            <form autoComplete="off" noValidate="novalidate">
                                <TitledTextField
                                    dataTest="iri"
                                    name="iri"
                                    title="IRI"
                                    autoFocus={true}
                                    required={true}
                                    text={iri}
                                    placeholder="Enter an IRI starting with http or https"
                                    maxLength={MAX_LONGIRI_LENGTH}
                                    adornment={
                                        <div style={{ display: 'inline-flex' }}>
                                            {additionalIcon}
                                            {icon}
                                        </div>
                                    }
                                    onChange={this.handleIRIChange} />
                            </form>
                            {checked && exists && resourceType ?
                                <TitledPanel margin={true} title="Resource Detail">
                                    {editedIRI && editedIRI !== iri &&
                                        <div>
                                            <Typography data-test="edited-iri-text" paragraph variant="body2">This resource had its IRI edited to the following value:</Typography>
                                            <Typography data-test="edited-iri" paragraph variant="body2">{editedIRI}</Typography>
                                        </div>
                                    }
                                    <Typography data-test="is-a" paragraph variant="body2">This resource is a {resourceType}.</Typography>
                                    {resourceType === "Collection" ?
                                        <div>
                                            <div data-test="collection-list">
                                                {
                                                    collections.filter(projectCollection => iri === projectCollection.id).map(collection => {
                                                        let link = APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/collection#project='
                                                            + project + "&collection=" + collection.id;
                                                        return <SchemeChip key={collection.id}
                                                            id={collection.id}
                                                            viewLang={defaultLang}
                                                            defaultLang={defaultLang}
                                                            isReadOnly={true}
                                                            graph={graph}
                                                            avatar={collection.type.includes("OrderedCollection") ?
                                                                <ReorderIcon data-test="ordered-icon" fontSize="small" />
                                                                :
                                                                <GrainIcon data-test="unordered-icon" fontSize="small" />
                                                            }
                                                            link={link} />;
                                                    })
                                                }
                                            </div>
                                        </div>
                                        :
                                        resourceSchemes && resourceSchemes.length > 0 ?
                                            <div>
                                                {(resourceType === "Concept" || resourceType === "Label") &&
                                                    <Typography paragraph variant="body2">This resource is in the following concept schemes:</Typography>
                                                }
                                                <div data-test="scheme-list">
                                                    {resourceSchemes.map((scheme) => {
                                                        const isComponent = schemes.some(projectScheme => scheme === projectScheme.id);
                                                        if (isComponent) {
                                                            let link = APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/taxonomy#project='
                                                                + project + "&taxonomy=" + scheme;
                                                            if (resourceType === "Concept") link += "&view=concept&concept=" + iriToLoad;
                                                            if (resourceType === "Label") link += "&view=label&label=" + iriToLoad;
                                                            return <SchemeChip key={scheme}
                                                                id={scheme}
                                                                viewLang={defaultLang}
                                                                defaultLang={defaultLang}
                                                                isReadOnly={true}
                                                                graph={graph}
                                                                link={link} />;
                                                        }
                                                    })}
                                                </div>
                                            </div>
                                            :
                                            <div data-test="floating-info">
                                                <Typography paragraph variant="body2">This resource is not in any concept scheme.</Typography>
                                                <SecondaryButton dataTest="view-floating" onClick={() => this.goToFloating(resourceType)}>View in Floating Items</SecondaryButton>
                                            </div>
                                    }
                                </TitledPanel>
                                :
                                checked &&
                                <TitledPanel margin={true} title="Resource Detail">
                                    <Typography data-test="not-found" paragraph variant="body2">This IRI cannot be found or is not a type that can be linked to.</Typography>
                                </TitledPanel>
                            }
                        </div>
                    </DialogContent>
                    <DialogActions className={classes.dialogActions}>
                        <CancelButton onClick={this.exitFindIRI} />
                        <PrimaryButton
                            disabled={!isValidIRI || checking || !isDirty}
                            onClick={this.findIRI}>Find</PrimaryButton>
                    </DialogActions>
                </Dialog>
            </React.Fragment>
        );
    }
}

FindIdentifierDialog.propTypes = {
    project: PropTypes.string.isRequired,
    graph: PropTypes.string.isRequired,
    history: PropTypes.object,
    classes: PropTypes.object,
    schemes: PropTypes.array,
    collections: PropTypes.array,
    defaultLang: PropTypes.string
};

export default withRouter(withStyles(styles)(FindIdentifierDialog));